import {
  TableColumnSettings,
  TableSettingsKey,
  UserTableSettings,
} from 'app/types';

export type TableColumnKey =
  | 'select'
  | 'number'
  | 'id'
  | 'wordType'
  | 'word'
  | 'transcription'
  | 'translation'
  | 'partOfSpeech'
  | 'definition'
  | 'levelBased'
  | 'addedAt'
  | 'infinitive'
  | 'infinitiveTranscription'
  | 'pastSimple'
  | 'pastSimpleTranscription'
  | 'pastParticiple'
  | 'pastParticipleTranscription'
  | 'isLearned'
  | 'isActiveSpeech'
  /**
   * Unified status column used by the v2 vocabulary view. Pairs with a
   * server-side enum once the BE consolidates the two booleans
   * (isLearned + isActiveSpeech) into one `status` value.
   */
  | 'status'
  | 'description';

export type TableColumnsVisibility = Partial<Record<TableColumnKey, boolean>>;

export interface TableColumnConfig {
  key: TableColumnKey;
  label: string;
  configurable?: boolean;
}

export interface ResolvedTableSettings {
  tableKey: TableSettingsKey;
  columns: TableColumnSettings[];
  highlightLearned: boolean;
  highlightActiveSpeech: boolean;
  pageSize: number;
}

export const DEFAULT_TABLE_PAGE_SIZE = 20;
export const TABLE_PAGE_SIZE_OPTIONS = [20, 50, 100];

export const TABLE_COLUMN_LABELS: Record<TableColumnKey, string> = {
  select: 'Selection',
  number: 'No',
  id: 'ID',
  wordType: 'Word type',
  word: 'Word',
  transcription: 'Transcription',
  translation: 'Translation',
  partOfSpeech: 'Part of speech',
  definition: 'Definition',
  levelBased: 'Level based',
  addedAt: 'Added at',
  infinitive: 'Infinitive',
  infinitiveTranscription: 'Infinitive Transc.',
  pastSimple: 'Past Simple',
  pastSimpleTranscription: 'Past Simple Transc.',
  pastParticiple: 'Past Participle',
  pastParticipleTranscription: 'Past Participle Transc.',
  isLearned: 'Learned',
  isActiveSpeech: 'Active speech',
  status: 'Status',
  description: 'Description',
};

export const DEFAULT_TABLE_COLUMN_ORDER: TableColumnKey[] = [
  'select',
  'number',
  'infinitive',
  'infinitiveTranscription',
  'word',
  'transcription',
  'translation',
  'partOfSpeech',
  'definition',
  'levelBased',
  'addedAt',
  'pastSimple',
  'pastSimpleTranscription',
  'pastParticiple',
  'pastParticipleTranscription',
  'status',
  'isLearned',
  'isActiveSpeech',
  'description',
];

export const createDefaultColumnSettings = (
  columns: TableColumnsVisibility,
): TableColumnSettings[] =>
  DEFAULT_TABLE_COLUMN_ORDER.filter((key) => key !== 'select')
    .filter((key) => columns[key])
    .map((key, index) => ({
      key,
      order: index,
      visible: true,
    }));

export const mergeTableSettings = (
  tableKey: TableSettingsKey,
  defaults: TableColumnSettings[],
  saved?: UserTableSettings,
): ResolvedTableSettings => {
  const savedColumns = saved?.columns || [];
  const defaultKeys = defaults.map(({ key }) => key);
  const savedByKey = savedColumns.reduce<Record<string, TableColumnSettings>>(
    (acc, column) => ({
      ...acc,
      [column.key]: column,
    }),
    {},
  );
  const savedColumnsInDefaults = savedColumns.filter((column) =>
    defaultKeys.includes(column.key),
  );
  const missingColumns = defaults.filter(({ key }) => !savedByKey[key]);
  const columns = [...savedColumnsInDefaults, ...missingColumns].map(
    (column, index) => ({
      ...column,
      order: index,
      visible:
        savedByKey[column.key]?.visible ??
        defaults.find(({ key }) => key === column.key)?.visible ??
        true,
    }),
  );

  return {
    tableKey,
    columns,
    highlightLearned: saved?.highlightLearned ?? true,
    highlightActiveSpeech: saved?.highlightActiveSpeech ?? true,
    pageSize: TABLE_PAGE_SIZE_OPTIONS.includes(saved?.pageSize || 0)
      ? saved?.pageSize || DEFAULT_TABLE_PAGE_SIZE
      : DEFAULT_TABLE_PAGE_SIZE,
  };
};

export const tableSettingsToColumnsVisibility = (
  settings: ResolvedTableSettings,
  requiredColumns: TableColumnsVisibility = {},
): TableColumnsVisibility =>
  ({
    ...settings.columns.reduce<TableColumnsVisibility>(
    (acc, column) => ({
      ...acc,
      [column.key]: column.visible,
    }),
      {},
    ),
    ...requiredColumns,
  });

export const getOrderedVisibleColumnKeys = (
  columns: TableColumnsVisibility,
  settings?: ResolvedTableSettings,
): TableColumnKey[] => {
  const configuredKeys =
    settings?.columns
      .filter((column) => column.visible && columns[column.key as TableColumnKey])
      .map((column) => column.key as TableColumnKey) || [];
  const missingVisibleKeys = DEFAULT_TABLE_COLUMN_ORDER.filter(
    (key) => columns[key] && !configuredKeys.includes(key),
  );

  return [
    ...missingVisibleKeys.filter((key) => key === 'select'),
    ...configuredKeys,
    ...missingVisibleKeys.filter((key) => key !== 'select'),
  ];
};
